/**
 * Scalar math for the simulation.
 *
 * Everything here is built from +, -, *, / and Math.floor/Math.sqrt, which
 * IEEE-754 and the JS spec require to be correctly rounded — the same inputs
 * produce the same bits on every engine. `Math.sin`, `Math.cos`, `Math.hypot`
 * and friends are explicitly NOT required to be correctly rounded, and V8,
 * JavaScriptCore and SpiderMonkey do disagree in the last ULP. A one-ULP
 * disagreement in a facing vector compounds over 7,200 ticks, and the whole
 * value of the input log is that a replay reproduces the match exactly. So the
 * sim uses `dsin`/`dcos` below and never the built-ins.
 */
import { TICK_HZ } from "@chaos/protocol";
export const PI = 3.141592653589793;
export const TWO_PI = 6.283185307179586;
export const HALF_PI = 1.5707963267948966;
/**
 * Deterministic sine. Range-reduces into [-PI/2, PI/2] then evaluates the
 * Taylor series through t^11, which is accurate to better than 1e-7 over that
 * interval — far tighter than anything gameplay can observe.
 */
export function dsin(x) {
    let t = x - TWO_PI * Math.floor(x / TWO_PI + 0.5);
    if (t > HALF_PI)
        t = PI - t;
    else if (t < -HALF_PI)
        t = -PI - t;
    const t2 = t * t;
    return (t *
        (1 +
            t2 *
                (-0.16666666666666666 +
                    t2 *
                        (0.008333333333333333 +
                            t2 * (-0.0001984126984126984 + t2 * (2.7557319223985893e-6 + t2 * -2.505210838544172e-8))))));
}
export function dcos(x) {
    return dsin(x + HALF_PI);
}
export function clamp(v, lo, hi) {
    return v < lo ? lo : v > hi ? hi : v;
}
export function lerp(a, b, t) {
    return a + (b - a) * t;
}
/** v1's `moveToward` (PhysicsEngine.js:46) — the shape of its acceleration curve. */
export function moveToward(current, target, maxDelta) {
    if (Math.abs(target - current) <= maxDelta)
        return target;
    return current + Math.sign(target - current) * maxDelta;
}
/** Shortest signed angular distance from `from` to `to`, in radians. */
export function shortAngleDist(from, to) {
    const d = ((to - from) % TWO_PI + TWO_PI) % TWO_PI;
    return d > PI ? d - TWO_PI : d;
}
/** Rotate `from` toward `to` by at most `maxDelta` radians. */
export function turnToward(from, to, maxDelta) {
    const d = shortAngleDist(from, to);
    if (Math.abs(d) <= maxDelta)
        return to;
    return from + Math.sign(d) * maxDelta;
}
/**
 * Milliseconds to whole ticks. Any positive duration is at least one tick, so a
 * 10ms cooldown never rounds down to "usable again immediately".
 */
export function msToTicks(ms) {
    if (ms <= 0)
        return 0;
    return Math.max(1, Math.round((ms * TICK_HZ) / 1000));
}
export function ticksToMs(ticks) {
    return (ticks * 1000) / TICK_HZ;
}
//# sourceMappingURL=math.js.map